import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MagnifyingGlassIcon,
  CrosshairIcon,
  SlidersHorizontalIcon,
  MountainsIcon,
  XIcon,
  MapTrifoldIcon,
  ListBulletsIcon,
  MapPinIcon,
  PathIcon,
  ArrowRightIcon,
  SquaresFourIcon,
  SparkleIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, STYLE_LABELS, ACCESS_LABELS } from "@/lib/apiClient";
import { buildDiscoveryParams, centersDiffer, mapAreaSearchCenter } from "@/lib/discoverySearch";
import {
  DEFAULT_RADIUS_KM,
  DISCOVERY_RADIUS_PRESETS,
  DISCOVERY_URL_KEYS,
  applyInterpretedCriteria,
  buildDiscoveryUrlParams,
  discoveryCriteriaContext,
  explicitInterpretedCriteriaKeys,
  readDiscoveryUrlState,
} from "@/lib/discoveryCriteriaState";
import { destinationTypeLabel, emptyGroupedSearchResults, hasGroupedSearchResults } from "@/lib/groupedSearch";
import { loadGuestPreferences, patchGuestPreferences } from "@/lib/guestPreferences";
import { formatDistanceKm } from "@/lib/units";
import { destinationCoverImage, destinationFallbackImage } from "@/lib/destinationCover";
import { Navbar } from "@/components/Navbar";
import { MapView } from "@/components/MapView";
import { ParkCard } from "@/components/ParkCard";
import { RideAssistant } from "@/components/RideAssistant";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { useUnitPreference } from "@/context/UnitPreferenceContext";
import { useRideMode } from "@/context/RideModeContext";
import { RIDE_MODE_OPTIONS } from "@/lib/rideMode";
import { parkPath, trailPath } from "@/lib/routes";
import { trackEvent } from "@/lib/analytics";
import { buildComparePath, MAX_COMPARE_SPOTS } from "@/lib/compareSpots";
import { buildDiscoveryResultEvidence } from "@/lib/discoveryResultReasons";
import { t as translate } from "@/i18n/messages";
import { useLanguage } from "@/i18n/useLanguage";

const RADII = DISCOVERY_RADIUS_PRESETS;
const TYPES = [
  { key: "everything", labelKey: "discover.everything" },
  { key: "bikepark", labelKey: "discover.bikeParks" },
  { key: "trailcenter", labelKey: "discover.trailCenters" },
  { key: "trail", labelKey: "discover.trails" },
];
const QUICK_STYLES = ["downhill", "enduro", "flow", "freeride", "jump", "xc", "family"];
const DIFFS = ["easy", "intermediate", "advanced", "expert"];
const DIFF_LABEL_KEYS = { easy: "discover.beginner", intermediate: "discover.intermediate", advanced: "discover.advanced", expert: "discover.expert" };

export default function Discover() {
  useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUrlState = useRef(
    readDiscoveryUrlState(searchParams, RIDE_MODE_OPTIONS.map((option) => option.value)),
  ).current;
  const hasDiscoveryUrlState = useRef(
    DISCOVERY_URL_KEYS.some((key) => searchParams.has(key)),
  ).current;
  const { user } = useAuth();
  const { unitSystem } = useUnitPreference();
  const { rideMode, setRideMode } = useRideMode();
  const [query, setQuery] = useState(initialUrlState.query);
  const [suggestions, setSuggestions] = useState(emptyGroupedSearchResults);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [origin, setOrigin] = useState(initialUrlState.center); // original geocoded/device location
  const [searchCenter, setSearchCenter] = useState(initialUrlState.center); // committed Discovery center
  const [pendingMapCenter, setPendingMapCenter] = useState(null); // moved map center, not searched yet
  const [radius, setRadius] = useState(initialUrlState.radius);
  const [type, setType] = useState(initialUrlState.type);
  const [styles, setStyles] = useState(initialUrlState.styles);
  const [access, setAccess] = useState(initialUrlState.access);
  const [difficulty, setDifficulty] = useState(initialUrlState.difficulty);
  const [dryWeather, setDryWeather] = useState(initialUrlState.dryWeather);
  const [openNow, setOpenNow] = useState(initialUrlState.openNow);
  const [minTrails, setMinTrails] = useState(initialUrlState.minTrails);
  const [maxDrive, setMaxDrive] = useState(initialUrlState.maxDrive);
  const [minRating, setMinRating] = useState(initialUrlState.minRating);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState(null);
  const [compareSpots, setCompareSpots] = useState([]);
  const [aiDiscovery, setAiDiscovery] = useState(null);
  const aiDiscoveryMode = aiDiscovery?.mode;
  const markAiCriteriaEdited = useCallback(() => {
    setAiDiscovery((current) => (
      current?.mode === "criteria"
        ? { ...current, manuallyEdited: true }
        : current?.mode === "legacy"
          ? null
          : current
    ));
  }, []);

  const [parks, setParks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [flyToCenter, setFlyToCenter] = useState(null);
  const [parkFocusKey, setParkFocusKey] = useState(0);
  const [fitKey, setFitKey] = useState(0);
  const [mobileView, setMobileView] = useState("list"); // list | map
  const [desktopResultsView, setDesktopResultsView] = useState(() => {
    try {
      return window.localStorage.getItem("bpf:desktop-results-view") === "list" ? "list" : "card";
    } catch {
      return "card";
    }
  });
  const listRef = useRef(null);
  const guestPreferencesReady = useRef(false);
  const restoredRideModeFromUrl = useRef(false);
  const defaultHomeApplied = useRef(false);
  const parksRequestId = useRef(0);
  const lastObservedSearch = useRef(searchParams.toString());
  const lastWrittenSearch = useRef(searchParams.toString());

  useEffect(() => {
    if (!restoredRideModeFromUrl.current && initialUrlState.rideMode) {
      restoredRideModeFromUrl.current = true;
      setRideMode(initialUrlState.rideMode);
    }
  }, [initialUrlState.rideMode, setRideMode]);

  useEffect(() => {
    if (!searchCenter && dryWeather) {
      setDryWeather(false);
    }
  }, [searchCenter, dryWeather]);

  useEffect(() => {
    if (user === false && !hasDiscoveryUrlState) {
      const restored = loadGuestPreferences().discovery;
      setRadius(restored.radius);
      setType(restored.type);
      setStyles(restored.styles);
      setAccess(restored.access);
      setDifficulty(restored.difficulty);
      setDryWeather(Boolean(restored.dryWeather));
      setOpenNow(restored.openNow);
      setMinTrails(restored.minTrails);
      setMaxDrive(restored.maxDrive);
      setMinRating(restored.minRating);
      guestPreferencesReady.current = true;
    } else {
      guestPreferencesReady.current = false;
    }
  }, [user, hasDiscoveryUrlState]);

  useEffect(() => {
    if (user !== false || !guestPreferencesReady.current) return;
    patchGuestPreferences({
      discovery: { radius, type, styles, access, difficulty, dryWeather, openNow, minTrails, maxDrive, minRating },
    });
  }, [user, radius, type, styles, access, difficulty, dryWeather, openNow, minTrails, maxDrive, minRating]);

  useEffect(() => {
    if (!user || user === false || hasDiscoveryUrlState || searchCenter || defaultHomeApplied.current) return;
    defaultHomeApplied.current = true;
    let active = true;

    api.get("/rider-profile")
      .then(({ data }) => {
        if (!active) return;
        const home = data?.home_location;
        if (!home || !Number.isFinite(home.lat) || !Number.isFinite(home.lng)) return;
        const location = { lat: home.lat, lng: home.lng, name: home.name || translateLabel("Home location") };
        setOrigin(location);
        setSearchCenter(location);
        setPendingMapCenter(null);
        setQuery(location.name);
        setFlyToCenter(null);
        setFitKey((key) => key + 1);
      })
      .catch(() => {});

    return () => { active = false; };
  }, [user, hasDiscoveryUrlState, searchCenter]);

  useEffect(() => {
    const currentSearch = searchParams.toString();

    // A browser back/forward navigation changed the URL before state was restored.
    // Do not immediately overwrite that URL with the previous in-memory filters.
    if (currentSearch !== lastObservedSearch.current && currentSearch !== lastWrittenSearch.current) return;

    const next = buildDiscoveryUrlParams({
      searchCenter,
      origin,
      radius,
      type,
      styles,
      access,
      difficulty,
      dryWeather,
      openNow,
      minTrails,
      maxDrive,
      minRating,
      rideMode,
    });
    const nextSearch = next.toString();
    lastObservedSearch.current = currentSearch;

    if (nextSearch !== currentSearch) {
      lastWrittenSearch.current = nextSearch;
      setSearchParams(next, { replace: true });
    } else {
      lastWrittenSearch.current = currentSearch;
    }
  }, [
    searchParams, searchCenter, origin, radius, type, styles, access, difficulty, dryWeather,
    openNow, minTrails, maxDrive, minRating, rideMode, setSearchParams,
  ]);

  useEffect(() => {
    const currentSearch = searchParams.toString();

    if (currentSearch === lastWrittenSearch.current) {
      lastObservedSearch.current = currentSearch;
      return;
    }
    if (currentSearch === lastObservedSearch.current) return;

    const restored = readDiscoveryUrlState(
      searchParams,
      RIDE_MODE_OPTIONS.map((option) => option.value),
    );
    lastObservedSearch.current = currentSearch;
    lastWrittenSearch.current = currentSearch;
    parksRequestId.current += 1;

    setAiDiscovery(null);
    setOrigin(restored.center);
    setSearchCenter(restored.center);
    setQuery(restored.query);
    setRadius(restored.radius);
    setType(restored.type);
    setStyles(restored.styles);
    setAccess(restored.access);
    setDifficulty(restored.difficulty);
    setDryWeather(restored.dryWeather);
    setOpenNow(restored.openNow);
    setMinTrails(restored.minTrails);
    setMaxDrive(restored.maxDrive);
    setMinRating(restored.minRating);
    setRideMode(restored.rideMode || "usual");
    setPendingMapCenter(null);
    setSelectedId(null);
    setHoveredId(null);
    setFlyToCenter(null);
    if (restored.center) setFitKey((key) => key + 1);
  }, [searchParams, setRideMode]);

  const hasStrictFilters =
    styles.length > 0 || access.length > 0 || difficulty.length > 0 || dryWeather || openNow ||
    type !== "everything" || minTrails > 0 || maxDrive > 0 || minRating > 0;

  const fetchParks = useCallback(async () => {
    if (aiDiscoveryMode === "legacy") {
      setLoading(false);
      return;
    }

    const requestId = ++parksRequestId.current;
    setLoading(true);
    try {
      const params = buildDiscoveryParams({
        searchCenter,
        radius,
        type,
        styles,
        access,
        difficulty,
        dryWeather,
        openNow,
        minTrails,
        maxDrive,
        minRating,
      });
      const { data } = await api.get("/parks", { params });
      if (requestId !== parksRequestId.current) return;

      const visibleParks = hasStrictFilters
        ? data.parks.filter((park) => park.discovery_bucket === "exact")
        : data.parks;
      setParks(visibleParks);
    } catch {
      if (requestId === parksRequestId.current) toast.error(translate("discover.loadError"));
    } finally {
      if (requestId === parksRequestId.current) setLoading(false);
    }
  }, [
    aiDiscoveryMode, searchCenter, radius, type, styles, access, difficulty, dryWeather,
    openNow, minTrails, maxDrive, minRating, hasStrictFilters,
  ]);

  useEffect(() => {
    fetchParks();
  }, [fetchParks]);

  useEffect(() => {
    try {
      window.localStorage.setItem("bpf:desktop-results-view", desktopResultsView);
    } catch {
      // Discovery still works when storage is unavailable.
    }
  }, [desktopResultsView]);

  useEffect(() => {
    if (query.trim().length < 3) {
      setSuggestions(emptyGroupedSearchResults());
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: query } });
        setSuggestions(data);
      } catch {
        setSuggestions(emptyGroupedSearchResults());
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const clearSuggestions = () => {
    setSuggestions(emptyGroupedSearchResults());
    setSuggestionsOpen(false);
  };

  const submitGroupedSearch = async () => {
    const searchQuery = query.trim();
    if (searchQuery.length < 2) return;
    setSuggestionsOpen(false);
    setLoading(true);
    try {
      const { data } = await api.get("/search", { params: { q: searchQuery } });
      const ids = [
        ...(data?.destinations || []).map((item) => item.id),
        ...(data?.trails || []).map((item) => item.destination?.id),
      ].filter(Boolean);
      const uniqueIds = [...new Set(ids)].slice(0, 20);
      const matches = await Promise.all(
        uniqueIds.map(async (id) => {
          try {
            const response = await api.get(`/parks/${id}`, {
              params: searchCenter ? { lat: searchCenter.lat, lng: searchCenter.lng } : {},
            });
            return response.data;
          } catch {
            return null;
          }
        }),
      );
      const visible = matches.filter(Boolean);
      setParks(visible);
      setSelectedId(null);
      setHoveredId(null);
      setPendingMapCenter(null);
      setFitKey((key) => key + 1);
      setSuggestions(emptyGroupedSearchResults());
      trackEvent("discovery_keyword_search", {
        query_length: searchQuery.length,
        result_count: visible.length,
      });
      if (!visible.length) toast(translate("discover.noMatching"));
    } catch {
      setSuggestions(emptyGroupedSearchResults());
      toast.error(translate("discover.searchError"));
    } finally {
      setLoading(false);
    }
  };

  const chooseLocation = (loc) => {
    markAiCriteriaEdited();
    const location = { lat: loc.lat, lng: loc.lng, name: loc.name };
    setOrigin(location);
    setSearchCenter(location);
    setPendingMapCenter(null);
    setQuery(loc.name.split(",")[0]);
    clearSuggestions();
    setFlyToCenter(null);
    setFitKey((k) => k + 1);
    trackEvent("discovery_search", { source: "place", radius_km: radius });
    toast.success(translateLabel("Searching near {place}", { place: loc.name.split(",")[0] }));
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error(translate("discover.geolocationUnsupported"));
    toast(translate("discover.locating"));
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        markAiCriteriaEdited();
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        let name = translate("discover.currentLocation");
        try {
          const response = await api.get("/reverse-geocode", { params: coords });
          if (response.data?.name) name = response.data.name.split(",")[0];
        } catch {
          // Discovery still works with coordinates if reverse geocoding is unavailable.
        }
        const location = { ...coords, name };
        setOrigin(location);
        setSearchCenter(location);
        setPendingMapCenter(null);
        setQuery(name);
        clearSuggestions();
        setFlyToCenter(null);
        setFitKey((k) => k + 1);
        trackEvent("discovery_search", { source: "geolocation", radius_km: radius });
        toast.success(translateLabel("Searching near {place}", { place: name }));
      },
      () => toast.error(translate("discover.locationError"))
    );
  };

  const handleViewportSettled = useCallback((center) => {
    setPendingMapCenter(centersDiffer(searchCenter, center) ? center : null);
  }, [searchCenter]);

  const searchThisArea = () => {
    if (!pendingMapCenter) return;
    markAiCriteriaEdited();
    setSearchCenter(mapAreaSearchCenter(pendingMapCenter));
    setPendingMapCenter(null);
    setFlyToCenter(null);
    trackEvent("discovery_search", { source: "map_area", radius_km: radius });
    toast.success(translateLabel("Searching this map area"));
  };

  const toggle = (arr, setArr, val) =>
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const clearSearchArea = () => {
    markAiCriteriaEdited();
    const hadSearchArea = Boolean(searchCenter || origin);
    setQuery("");
    setOrigin(null);
    setSearchCenter(null);
    setPendingMapCenter(null);
    setSelectedId(null);
    setFlyToCenter(null);
    setRadius(DEFAULT_RADIUS_KM);
    clearSuggestions();
    if (hadSearchArea) trackEvent("discovery_search_area_cleared", { previous_radius_km: radius });
  };

  const committedFilterSnapshot = () => ({
    radius,
    type,
    styles: [...styles],
    access: [...access],
    difficulty: [...difficulty],
    dryWeather,
    openNow,
    minTrails,
    maxDrive,
    minRating,
    rideMode,
  });

  const handleFiltersOpenChange = (open) => {
    setFilterDraft(open ? committedFilterSnapshot() : null);
    setFiltersOpen(open);
  };

  const handleMobileFiltersToggle = () => {
    if (mobileFiltersOpen) {
      setMobileFiltersOpen(false);
      setFilterDraft(null);
      return;
    }
    setFilterDraft(committedFilterSnapshot());
    setMobileFiltersOpen(true);
  };

  const updateMobileFilter = (patch) => {
    markAiCriteriaEdited();
    setFilterDraft((current) => ({ ...(current || committedFilterSnapshot()), ...patch }));

    if (Object.prototype.hasOwnProperty.call(patch, "type")) setType(patch.type);
    if (Object.prototype.hasOwnProperty.call(patch, "dryWeather")) setDryWeather(patch.dryWeather);
    if (Object.prototype.hasOwnProperty.call(patch, "openNow")) setOpenNow(patch.openNow);
    if (Object.prototype.hasOwnProperty.call(patch, "minTrails")) setMinTrails(patch.minTrails);
    if (Object.prototype.hasOwnProperty.call(patch, "maxDrive")) setMaxDrive(patch.maxDrive);
    if (Object.prototype.hasOwnProperty.call(patch, "minRating")) setMinRating(patch.minRating);
    if (Object.prototype.hasOwnProperty.call(patch, "rideMode")) setRideMode(patch.rideMode);
  };

  const toggleMobileFilterList = (key, value) => {
    markAiCriteriaEdited();
    const setter = key === "styles" ? setStyles : key === "access" ? setAccess : setDifficulty;
    setter((current) => (
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    ));
    setFilterDraft((current) => {
      const draft = current || committedFilterSnapshot();
      const values = draft[key] || [];
      return {
        ...draft,
        [key]: values.includes(value) ? values.filter((item) => item !== value) : [...values, value],
      };
    });
  };

  const clearMobileFilters = () => {
    markAiCriteriaEdited();
    const radiusChanged = radius !== DEFAULT_RADIUS_KM;
    setRadius(DEFAULT_RADIUS_KM);
    setType("everything");
    setStyles([]);
    setAccess([]);
    setDifficulty([]);
    setDryWeather(false);
    setOpenNow(false);
    setMinTrails(0);
    setMaxDrive(0);
    setMinRating(0);
    setFilterDraft({
      ...committedFilterSnapshot(),
      radius: DEFAULT_RADIUS_KM,
      type: "everything",
      styles: [],
      access: [],
      difficulty: [],
      dryWeather: false,
      openNow: false,
      minTrails: 0,
      maxDrive: 0,
      minRating: 0,
    });
    if (radiusChanged && searchCenter) setFitKey((key) => key + 1);
    trackEvent("discovery_filters_cleared", { source: "mobile_inline" });
  };

  const updateFilterDraft = (patch) => {
    setFilterDraft((current) => ({ ...(current || committedFilterSnapshot()), ...patch }));
  };

  const toggleFilterDraftList = (key, value) => {
    setFilterDraft((current) => {
      const draft = current || committedFilterSnapshot();
      const values = draft[key] || [];
      return {
        ...draft,
        [key]: values.includes(value) ? values.filter((item) => item !== value) : [...values, value],
      };
    });
  };

  const clearFilterDraft = () => {
    updateFilterDraft({
      radius: DEFAULT_RADIUS_KM,
      type: "everything",
      styles: [],
      access: [],
      difficulty: [],
      dryWeather: false,
      openNow: false,
      minTrails: 0,
      maxDrive: 0,
      minRating: 0,
    });
  };

  const handleMapSelect = (id) => {
    const selectedPark = parks.find((candidate) => candidate.id === id);
    trackEvent("park_select", {
      source: "map",
      park_id: String(id),
      park_name: selectedPark?.name || "",
    });
    setSelectedId(id);
    const el = listRef.current?.querySelector(`[data-park-id="${id}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleListSelect = (id) => {
    setSelectedId(id);
    const park = parks.find((candidate) => candidate.id === id);
    trackEvent("park_select", {
      source: "list",
      park_id: String(id),
      park_name: park?.name || "",
    });
    if (park) {
      setFlyToCenter([park.lat, park.lng]);
      setParkFocusKey((key) => key + 1);
    }
  };

  const toggleCompareSpot = (park) => {
    const selected = compareSpots.some((candidate) => candidate.id === park.id);
    if (selected) {
      setCompareSpots((current) => current.filter((candidate) => candidate.id !== park.id));
      trackEvent("compare_spot_removed", { park_id: String(park.id), source: "discovery" });
      return;
    }
    if (compareSpots.length >= MAX_COMPARE_SPOTS) {
      toast(`You can compare up to ${MAX_COMPARE_SPOTS} spots`);
      return;
    }
    setCompareSpots((current) => [...current, park]);
    trackEvent("compare_spot_added", {
      park_id: String(park.id),
      park_name: park.name || "",
      source: "discovery",
    });
  };

  const openComparison = () => {
    if (compareSpots.length < 2) {
      toast(translate("discover.compareNeedTwo"));
      return;
    }
    const returnTo = `${window.location.pathname}${window.location.search}`;
    navigate(buildComparePath(compareSpots, { searchCenter, returnTo }));
  };

  const countActiveFilters = (values) =>
    values.styles.length + values.access.length + values.difficulty.length + (values.dryWeather ? 1 : 0) + (values.openNow ? 1 : 0) +
    (values.type !== "everything" ? 1 : 0) + (values.minTrails ? 1 : 0) +
    (values.maxDrive ? 1 : 0) + (values.minRating ? 1 : 0);

  const activeFilterCount = countActiveFilters({
    styles, access, difficulty, dryWeather, openNow, type, minTrails, maxDrive, minRating,
  });

  const filterControls = filterDraft || committedFilterSnapshot();

  const applyFilterDraft = () => {
    markAiCriteriaEdited();
    const next = filterDraft || committedFilterSnapshot();
    const radiusChanged = next.radius !== radius;
    setRadius(next.radius);
    setType(next.type);
    setStyles(next.styles);
    setAccess(next.access);
    setDifficulty(next.difficulty);
    setDryWeather(next.dryWeather);
    setOpenNow(next.openNow);
    setMinTrails(next.minTrails);
    setMaxDrive(next.maxDrive);
    setMinRating(next.minRating);
    setRideMode(next.rideMode);
    if (radiusChanged && searchCenter) setFitKey((key) => key + 1);
    trackEvent("discovery_filters_applied", {
      active_filter_count: countActiveFilters(next),
      result_count_before_apply: parks.length,
    });
    setFilterDraft(null);
    setFiltersOpen(false);
    setMobileFiltersOpen(false);
  };

  const handleAiCriteria = useCallback((result) => {
    const next = applyInterpretedCriteria({
      origin,
      searchCenter,
      query,
      radius,
      type,
      styles,
      access,
      difficulty,
      openNow,
      minTrails,
      minRating,
    }, result?.criteria || {}, {
      removedCriteria: result?.removed_criteria || [],
      resetSearch: Boolean(result?.reset_search),
    });

    // Invalidate an older ordinary /parks request before the new criteria effect starts.
    parksRequestId.current += 1;
    setAiDiscovery({
      mode: "criteria",
      explanation: result?.explanation || "",
      appliedKeys: explicitInterpretedCriteriaKeys(result?.criteria || {}),
      preferences: result?.preferences || {},
    });

    setOrigin(next.origin);
    setSearchCenter(next.searchCenter);
    setQuery(next.query);
    setRadius(next.radius);
    setType(next.type);
    setStyles(next.styles);
    setAccess(next.access);
    setDifficulty(next.difficulty);
    setOpenNow(next.openNow);
    setMinTrails(next.minTrails);
    setMinRating(next.minRating);
    setPendingMapCenter(null);
    setSelectedId(null);
    setHoveredId(null);
    setCompareSpots([]);
    setFlyToCenter(null);
    setFitKey((key) => key + 1);
    setMobileView("list");

    trackEvent("ai_discovery_criteria_applied", {
      criteria_count: explicitInterpretedCriteriaKeys(result?.criteria || {}).length,
      has_origin: Boolean(result?.criteria?.origin),
      radius_km: result?.criteria?.radius_km ?? null,
    });
  }, [
    origin, searchCenter, query, radius, type, styles, access, difficulty,
    openNow, minTrails, minRating,
  ]);

  const handleAiResults = useCallback((result) => {
    const resultOrigins = result?.origins || (result?.origin ? [result.origin] : []);
    if (resultOrigins.length > 1) {
      const params = new URLSearchParams();
      resultOrigins.slice(0, 4).forEach((item, index) => {
        if (item?.label) params.set(`o${index + 1}`, item.label);
      });
      if (Number.isFinite(result?.intent?.max_drive_minutes)) {
        params.set("max", String(result.intent.max_drive_minutes));
      }
      const supportedAccess = (result?.intent?.access || []).filter((value) => ["lift", "pedal"].includes(value));
      if (supportedAccess.length === 1) params.set("access", supportedAccess[0]);
      if (result?.intent?.styles?.length) params.set("styles", result.intent.styles.join(","));
      if (result?.intent?.date) params.set("date", result.intent.date);

      const unsupportedGroupCriteria = [
        ...(result?.intent?.wants?.length ? ["trail preferences"] : []),
        ...(result?.intent?.dry_weather ? ["dry-weather preference"] : []),
      ];
      navigate(`/group-discovery?${params.toString()}`);
      if (unsupportedGroupCriteria.length) {
        toast("Moved to Group Discovery", {
          description: `Preserved group origins and drive criteria. ${unsupportedGroupCriteria.join(" and ")} are not group filters yet.`,
        });
      } else {
        toast.success(translateLabel("Moved to Group Discovery"));
      }
      return;
    }

    const recommendations = result?.recommendations || [];
    const aiParks = recommendations.map((recommendation) => ({
      ...recommendation.park,
      drive_minutes: recommendation.travel?.drive_minutes ?? recommendation.park?.drive_minutes,
      drive_km: recommendation.travel?.drive_km ?? recommendation.park?.drive_km,
      recommendation_reason:
        recommendation.decision_reasons?.[0]
        || recommendation.recommendation_reason
        || recommendation.park?.recommendation_reason,
      ai_weather: recommendation.weather || null,
      ai_decision_reasons: recommendation.decision_reasons || [],
      ai_warnings: recommendation.warnings || [],
      ai_want_match_status: recommendation.want_match_status || null,
    }));

    const aiOrigin = result?.origin
      ? { lat: result.origin.lat, lng: result.origin.lng, name: result.origin.label || "AI search origin" }
      : null;

    parksRequestId.current += 1;
    setAiDiscovery({
      mode: "legacy",
      summary: result?.summary || "",
      matchKind: result?.match_kind || "EXACT",
      relaxedConstraints: result?.relaxed_constraints || [],
      unverifiedReasons: result?.unverified_reasons || [],
      evaluationComplete: result?.evaluation_complete !== false,
      intent: result?.intent || null,
      date: result?.date || null,
      origins: result?.origins || (result?.origin ? [result.origin] : []),
    });
    setParks(aiParks);
    setLoading(false);
    setSelectedId(null);
    setHoveredId(null);
    setCompareSpots([]);

    if (aiOrigin) {
      setOrigin(aiOrigin);
      setSearchCenter(aiOrigin);
      setPendingMapCenter(null);
      setQuery(aiOrigin.name);
    }

    setFlyToCenter(null);
    setFitKey((key) => key + 1);
    setMobileView("list");
  }, [navigate]);

  const clearAiResults = () => {
    setAiDiscovery(null);
    setSelectedId(null);
    setHoveredId(null);
    setCompareSpots([]);
  };

  const searchLabel = searchCenter?.name === "this map area"
    ? "this map area"
    : origin?.name?.split(",")[0] ?? searchCenter?.name;

  const currentAiCriteria = discoveryCriteriaContext({
    searchCenter,
    radius,
    type,
    styles,
    access,
    difficulty,
    openNow,
    minTrails,
    minRating,
  });

  const resultEvidenceCriteria = {
    radius: searchCenter ? radius : 0,
    type,
    styles,
    access,
    difficulty,
    openNow,
    minTrails,
    minRating,
  };
  const resultEvidencePreferences = aiDiscovery?.mode === "criteria" ? aiDiscovery.preferences : {};

  const legacyAiActive = aiDiscoveryMode === "legacy";
  const radiusOptions = [...new Set([
    ...RADII,
    ...(searchCenter && !RADII.includes(radius) ? [radius] : []),
  ])].sort((a, b) => a - b);

  const activeCriteria = [
    searchCenter ? { id: "origin", key: "origin", label: `From ${searchLabel || searchCenter.name || "selected location"}` } : null,
    searchCenter ? {
      id: "radius",
      key: "radius",
      label: `Radius ${formatDistanceKm(radius, unitSystem)}`,
      removable: radius !== DEFAULT_RADIUS_KM,
    } : null,
    type !== "everything" ? {
      id: "type",
      key: "type",
      label: TYPES.find((item) => item.key === type)?.label || type,
    } : null,
    ...styles.map((value) => ({ id: `style-${value}`, key: "styles", value, label: STYLE_LABELS[value] || value })),
    ...access.map((value) => ({ id: `access-${value}`, key: "access", value, label: ACCESS_LABELS[value] || value })),
    ...difficulty.map((value) => ({ id: `difficulty-${value}`, key: "difficulty", value, label: DIFF_LABEL_KEYS[value] ? translate(DIFF_LABEL_KEYS[value]) : value })),
    dryWeather ? { id: "dryWeather", key: "dryWeather", label: translateLabel("Dry today") } : null,
    openNow ? { id: "openNow", key: "openNow", label: translateLabel("Open now") } : null,
    minTrails ? { id: "minTrails", key: "minTrails", label: `At least ${minTrails} trails` } : null,
    maxDrive ? { id: "maxDrive", key: "maxDrive", label: `Max drive ${maxDrive} min` } : null,
    minRating ? { id: "minRating", key: "minRating", label: `Rating ${minRating}+` } : null,
  ].filter(Boolean);

  const removeActiveCriterion = (criterion) => {
    // Keep AI provenance visible while removing only the selected criterion.
    // Manual filter edits may end the AI-applied state, but dismissing one chip must not.
    if (criterion.key === "origin") {
      const hadSearchArea = Boolean(searchCenter || origin);
      setQuery("");
      setOrigin(null);
      setSearchCenter(null);
      setPendingMapCenter(null);
      setSelectedId(null);
      setFlyToCenter(null);
      setRadius(DEFAULT_RADIUS_KM);
      clearSuggestions();
      if (hadSearchArea) trackEvent("discovery_search_area_cleared", { previous_radius_km: radius });
      return;
    }
    if (criterion.key === "radius") {
      setRadius(DEFAULT_RADIUS_KM);
      if (searchCenter) setFitKey((key) => key + 1);
      return;
    }
    if (criterion.key === "type") setType("everything");
    if (criterion.key === "styles") setStyles((current) => current.filter((item) => item !== criterion.value));
    if (criterion.key === "access") setAccess((current) => current.filter((item) => item !== criterion.value));
    if (criterion.key === "difficulty") setDifficulty((current) => current.filter((item) => item !== criterion.value));
    if (criterion.key === "dryWeather") setDryWeather(false);
    if (criterion.key === "openNow") setOpenNow(false);
    if (criterion.key === "minTrails") setMinTrails(0);
    if (criterion.key === "maxDrive") setMaxDrive(0);
    if (criterion.key === "minRating") setMinRating(0);
  };

  const openCriteriaEditor = () => {
    setFilterDraft(committedFilterSnapshot());
    if (window.matchMedia?.("(min-width: 768px)").matches) {
      setFiltersOpen(true);
    } else {
      setMobileFiltersOpen(true);
    }
  };

  const hasSuggestions = hasGroupedSearchResults(suggestions);
  const selectedPark = parks.find((park) => park.id === selectedId) || null;

  return (
    <div className="relative h-full w-full overflow-hidden bg-[#09090b]">
      <Navbar />

      {/* Map layer */}
      <div className="absolute inset-0 z-0">
        <MapView
          parks={parks}
          origin={searchCenter}
          radiusKm={legacyAiActive ? null : (searchCenter ? radius : null)}
          selectedId={selectedId}
          hoveredId={hoveredId}
          onSelect={handleMapSelect}
          selectedParkOverlay={selectedPark ? (
            <MapSelectionPreview
              park={selectedPark}
              unitSystem={unitSystem}
              discoveryCriteria={resultEvidenceCriteria}
              discoveryPreferences={resultEvidencePreferences}
              onClose={() => setSelectedId(null)}
            />
          ) : null}
          onHover={setHoveredId}
          onViewportSettled={handleViewportSettled}
          flyToCenter={flyToCenter}
          flyToKey={parkFocusKey}
          fitKey={fitKey}
          cluster
        />
      </div>

      <AnimatePresence>
        {pendingMapCenter && (
          <motion.button
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            onClick={searchThisArea}
            data-testid="search-this-area-btn"
            className="absolute left-1/2 top-20 z-[1050] -translate-x-1/2 rounded-full bg-[#CCFF00] px-5 py-2.5 text-sm font-bold text-black shadow-xl transition-transform hover:scale-[1.02] active:scale-95"
          >
            Search this area
          </motion.button>
        )}
      </AnimatePresence>

      <RideAssistant
        currentOrigin={searchCenter}
        currentCriteria={currentAiCriteria}
        onCriteria={handleAiCriteria}
        onResults={handleAiResults}
        onGroupDiscovery={(interpretation) => {
          const intent = interpretation?.intent || {};
          const params = new URLSearchParams();
          (intent.origins || []).slice(0, 4).forEach((item, index) => {
            if (item) params.set(`o${index + 1}`, item);
          });
          if (Number.isFinite(intent.max_drive_minutes)) params.set("max", String(intent.max_drive_minutes));
          const supportedAccess = (intent.access || []).filter((value) => ["lift", "pedal"].includes(value));
          if (supportedAccess.length === 1) params.set("access", supportedAccess[0]);
          if (intent.styles?.length) params.set("styles", intent.styles.join(","));
          if (intent.date) params.set("date", intent.date);
          trackEvent("ride_assistant_group_handoff", {
            origin_count: intent.origins?.length || 0,
            has_max_drive: intent.max_drive_minutes != null,
            has_date: Boolean(intent.date),
          });
          navigate(`/group-discovery?${params.toString()}`);
        }}
      />

      {/* Mobile toggle */}
      <button
        onClick={() => setMobileView((v) => (v === "list" ? "map" : "list"))}
        data-testid="mobile-view-toggle"
        className="absolute bottom-5 left-1/2 z-[1250] flex -translate-x-1/2 items-center gap-2 rounded-full bg-[#CCFF00] px-5 py-3 text-sm font-bold text-black shadow-xl active:scale-95 md:hidden"
      >
        {mobileView === "list" ? <><MapTrifoldIcon size={18} weight="bold" /> {translateLabel("Map")}</> : <><ListBulletsIcon size={18} weight="bold" /> {translateLabel("List")}</>}
      </button>

      {/* Left floating panel */}
      <div
        className={`absolute bottom-0 left-0 top-0 z-[1100] flex w-full flex-col pt-16 md:w-[27rem] md:p-3 md:pt-20 ${
          mobileView === "map" ? "hidden md:flex" : "flex"
        }`}
      >
        <div className="flex h-full flex-col overflow-hidden rounded-none border border-white/10 bg-black/70 backdrop-blur-2xl md:rounded-3xl">
          {/* Search header */}
          <div className="space-y-2 border-b border-white/10 p-3 md:p-3">
            <div className="relative">
              <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2.5">
                <MagnifyingGlassIcon size={18} className="text-zinc-400" />
                <input
                  value={query}
                  onFocus={() => setSuggestionsOpen(true)}
                  onChange={(e) => { setQuery(e.target.value); setSuggestionsOpen(true); }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      submitGroupedSearch();
                    }
                    if (event.key === "Escape") setSuggestionsOpen(false);
                  }}
                  placeholder={translate("discover.searchPlaceholder")}
                  data-testid="location-search-input"
                  className="w-full bg-transparent text-sm font-medium text-white placeholder:text-zinc-500 focus:outline-none"
                />
                {(query || searchCenter) && (
                  <button
                    type="button"
                    onClick={clearSearchArea}
                    data-testid="clear-search-area-btn"
                    aria-label={searchCenter ? translate("discover.clearSearchArea") : translate("discover.clearSearch")}
                    title={searchCenter ? translate("discover.clearSearchArea") : translate("discover.clearSearch")}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    <XIcon size={15} weight="bold" />
                  </button>
                )}
                <button
                  onClick={useMyLocation}
                  data-testid="use-location-btn"
                  title={translate("discover.useLocation")}
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-[#CCFF00] text-black transition-transform active:scale-90"
                >
                  <CrosshairIcon size={16} weight="bold" />
                </button>
              </div>
              <AnimatePresence>
                {suggestionsOpen && hasSuggestions && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    className="absolute left-0 right-0 top-full z-[1200] mt-2 max-h-[26rem] overflow-y-auto rounded-2xl border border-white/10 bg-[#1c1c1f] py-1 shadow-2xl"
                    data-testid="grouped-search-suggestions"
                  >
                    <div className="flex items-center justify-between border-b border-white/[0.06] px-3 py-2">
                      <span className="text-[10px] font-black uppercase tracking-[0.14em] text-zinc-500">
                        {translateLabel("Search results")}
                      </span>
                      <span className="text-[10px] text-zinc-600">{translate("discover.clickResult")}</span>
                    </div>
                    <SearchGroup title={translateLabel("Destinations")} visible={suggestions.destinations.length > 0}>
                      {suggestions.destinations.map((item) => (
                        <SearchResultButton
                          key={`destination-${item.id}`}
                          to={parkPath(item)}
                          onClick={() => {
                            trackEvent("search_result_open", {
                              result_type: "park",
                              item_id: String(item.id),
                              item_name: item.name || "",
                              query_length: query.trim().length,
                            });
                            clearSuggestions();
                            setQuery(item.name);
                          }}
                          testid="destination-search-result"
                          title={item.name}
                          subtitle={[translateLabel(destinationTypeLabel(item.primary_type)), item.city, item.country].filter(Boolean).join(" · ")}
                          icon={<MountainsIcon size={16} />}
                        />
                      ))}
                    </SearchGroup>
                    <SearchGroup title={translateLabel("Trails")} visible={suggestions.trails.length > 0}>
                      {suggestions.trails.map((item) => (
                        <SearchResultButton
                          key={`trail-${item.id}`}
                          to={trailPath(item)}
                          onClick={() => {
                            trackEvent("search_result_open", {
                              result_type: "trail",
                              item_id: String(item.id),
                              item_name: item.name || "",
                              query_length: query.trim().length,
                            });
                            clearSuggestions();
                            setQuery(item.name);
                          }}
                          testid="trail-search-result"
                          title={item.name}
                          subtitle={[item.difficulty?.toLowerCase(), item.destination?.name].filter(Boolean).join(" · ")}
                          icon={<ListBulletsIcon size={16} />}
                        />
                      ))}
                    </SearchGroup>
                    <SearchGroup title={translateLabel("Places")} visible={suggestions.places.length > 0}>
                      {suggestions.places.map((item, index) => (
                        <SearchResultButton
                          key={`place-${item.name}-${index}`}
                          onClick={() => chooseLocation(item)}
                          testid="place-search-result"
                          title={item.name}
                          subtitle={translate("discover.useAsCenter")}
                          icon={<CrosshairIcon size={16} />}
                        />
                      ))}
                    </SearchGroup>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Radius */}
            <div className="hidden md:block">
              <div className="label-tiny mb-1.5">{translate("discover.searchRadius")}</div>
              <div className="flex gap-1.5">
                {radiusOptions.map((r) => (
                  <button
                    key={r}
                    disabled={!searchCenter}
                    onClick={() => { markAiCriteriaEdited(); setRadius(r); setFitKey((k) => k + 1); }}
                    data-testid={`radius-${r}`}
                    className={`flex-1 rounded-full py-1.5 text-xs font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${
                      !legacyAiActive && radius === r ? "bg-[#CCFF00] text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10"
                    }`}
                  >
                    {formatDistanceKm(r, unitSystem)}
                  </button>
                ))}
              </div>
              {!searchCenter && <p className="mt-1.5 text-[11px] font-semibold text-amber-300">{translate("discover.chooseLocation")}</p>}
            </div>

            {/* Type */}
            <div className="hidden gap-1.5 md:flex md:flex-wrap">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => { markAiCriteriaEdited(); setType(t.key); }}
                  data-testid={`type-${t.key}`}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                    type === t.key ? "bg-white text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10"
                  }`}
                >
                  {translate(t.labelKey)}
                </button>
              ))}
            </div>

            {/* Quick style pills + advanced */}
            <div className="hidden">
              <button
                onClick={() => { markAiCriteriaEdited(); setOpenNow((v) => !v); }}
                data-testid="quick-open-now"
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                  openNow ? "bg-emerald-500 text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10"
                }`}
              >
                {translateLabel("Open now")}
              </button>
              {QUICK_STYLES.map((s) => (
                <button
                  key={s}
                  onClick={() => { markAiCriteriaEdited(); toggle(styles, setStyles, s); }}
                  data-testid={`quick-style-${s}`}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                    styles.includes(s) ? "bg-[#CCFF00] text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10"
                  }`}
                >
                  {STYLE_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          {/* Results header */}
          <div className="flex items-center justify-between gap-1 px-3 py-2 md:flex-col md:items-stretch md:px-3">
            <span className="min-w-0 truncate text-sm font-bold text-white" data-testid="results-count">
              {loading
                ? "Searching…"
                : legacyAiActive
                  ? `${parks.length} advanced AI match${parks.length === 1 ? "" : "es"}`
                  : translateLabel(parks.length === 1 ? "{count} riding spot" : "{count} riding spots", { count: parks.length })}
              {searchLabel && !loading && !legacyAiActive && <span className="ml-1 font-medium text-zinc-400">{" "}{translateLabel("near {place}", { place: searchLabel })}</span>}
            </span>
            <div className="flex shrink-0 items-center gap-1.5 md:justify-between">
            {/* Mobile radius shares the results toolbar with Filters. */}
            <label className="inline-flex h-9 shrink-0 items-center gap-1 rounded-full bg-white/5 px-2 text-xs font-bold text-zinc-300 md:hidden">
              <span className="sr-only">{translate("discover.radius")}</span>
              <select
                aria-label={translateLabel("Search radius")}
                data-testid="mobile-radius-select"
                value={legacyAiActive ? "" : radius}
                disabled={!searchCenter}
                onChange={(event) => {
                  const nextRadius = Number(event.target.value);
                  markAiCriteriaEdited();
                  setRadius(nextRadius);
                  setFilterDraft((current) => current ? { ...current, radius: nextRadius } : current);
                  setFitKey((k) => k + 1);
                }}
                className="bg-transparent font-bold text-[#CCFF00] outline-none disabled:text-zinc-600"
              >
                {legacyAiActive && <option value="" disabled className="bg-[#1c1c1f] text-zinc-500">{translate("discover.radius")}</option>}
                {radiusOptions.map((r) => (
                  <option key={r} value={r} className="bg-[#1c1c1f] text-white">
                    {formatDistanceKm(r, unitSystem)}
                  </option>
                ))}
              </select>
            </label>


              <div className="hidden items-center rounded-full border border-white/10 bg-white/5 p-0.5 md:flex" role="group" aria-label={translate("discover.resultsView")}>
                <button
                  type="button"
                  onClick={() => setDesktopResultsView("card")}
                  aria-label={translate("discover.cardView")}
                  aria-pressed={desktopResultsView === "card"}
                  title={translate("discover.cardView")}
                  data-testid="desktop-card-view"
                  className={`flex h-7 w-8 items-center justify-center rounded-full transition-colors ${
                    desktopResultsView === "card" ? "bg-white text-black" : "text-zinc-400 hover:text-white"
                  }`}
                >
                  <SquaresFourIcon size={15} weight="bold" />
                </button>
                <button
                  type="button"
                  onClick={() => setDesktopResultsView("list")}
                  aria-label={translate("discover.listView")}
                  aria-pressed={desktopResultsView === "list"}
                  title={translate("discover.listView")}
                  data-testid="desktop-list-view"
                  className={`flex h-7 w-8 items-center justify-center rounded-full transition-colors ${
                    desktopResultsView === "list" ? "bg-white text-black" : "text-zinc-400 hover:text-white"
                  }`}
                >
                  <ListBulletsIcon size={15} weight="bold" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleMobileFiltersToggle}
                  aria-expanded={mobileFiltersOpen}
                  data-testid="open-mobile-filters-btn"
                  className={`flex h-9 items-center gap-1 rounded-full px-2 text-xs font-bold transition-colors md:hidden ${
                    mobileFiltersOpen
                      ? "bg-[#CCFF00] text-black"
                      : "bg-white/5 text-white hover:bg-white/10"
                  }`}
                >
                  <SlidersHorizontalIcon size={14} /> {translateLabel("Filters")}
                  {activeFilterCount > 0 && (
                    <span className={`flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] ${
                      mobileFiltersOpen ? "bg-black text-[#CCFF00]" : "bg-[#CCFF00] text-black"
                    }`}>
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                <div className="hidden md:block">
                <Sheet open={filtersOpen} onOpenChange={handleFiltersOpenChange}>
                <SheetTrigger asChild>
                  <button
                    data-testid="open-filters-btn"
                    className="flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-white/10"
                  >
                    <SlidersHorizontalIcon size={14} /> {translateLabel("Filters")}
                    {activeFilterCount > 0 && (
                      <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-[#CCFF00] px-1 text-[10px] text-black">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                </SheetTrigger>
              <SheetContent side="responsive" className="z-[1400] flex flex-col overflow-hidden border-white/10 bg-[#121214] text-white md:max-w-md">
                <SheetHeader className="shrink-0 pb-1">
                  <SheetTitle className="font-display text-2xl uppercase text-white">{translate("discover.filters")}</SheetTitle>
                </SheetHeader>
                <div className="-mx-6 flex-1 overflow-y-auto overscroll-contain px-6 pb-4">
                <div className="md:hidden">
                  <FilterGroup title={translateLabel("Search radius")}>
                    {radiusOptions.map((r) => (
                      <PillToggle
                        key={r}
                        active={!legacyAiActive && filterControls.radius === r}
                        disabled={!searchCenter}
                        onClick={() => updateFilterDraft({ radius: r })}
                        testid={`filter-radius-${r}`}
                      >
                        {formatDistanceKm(r, unitSystem)}
                      </PillToggle>
                    ))}
                  </FilterGroup>
                  {!searchCenter && <p className="-mt-3 mb-5 text-xs font-semibold text-amber-300">{translate("discover.locationFirstRadius")}</p>}
                  <FilterGroup title={translateLabel("Destination type")}>
                    {TYPES.map((t) => (
                      <PillToggle
                        key={t.key}
                        active={filterControls.type === t.key}
                        onClick={() => updateFilterDraft({ type: t.key })}
                        testid={`filter-type-${t.key}`}
                      >
                        {translate(t.labelKey)}
                      </PillToggle>
                    ))}
                  </FilterGroup>
                </div>
                <FilterGroup title={translateLabel("Availability")}>
                  <PillToggle
                    active={filterControls.openNow}
                    onClick={() => updateFilterDraft({ openNow: !filterControls.openNow })}
                    testid="filter-open-now"
                  >
                    {translateLabel("Open now")}
                  </PillToggle>
                </FilterGroup>
                <FilterGroup title={translateLabel("Riding style")}>
                  {QUICK_STYLES.map((s) => (
                    <PillToggle key={s} active={filterControls.styles.includes(s)} onClick={() => toggleFilterDraftList("styles", s)} testid={`filter-style-${s}`}>
                      {STYLE_LABELS[s]}
                    </PillToggle>
                  ))}
                </FilterGroup>
                <FilterGroup title={translateLabel("Access")}>
                  {Object.keys(ACCESS_LABELS).map((a) => (
                    <PillToggle key={a} active={filterControls.access.includes(a)} onClick={() => toggleFilterDraftList("access", a)} testid={`filter-access-${a}`}>
                      {ACCESS_LABELS[a]}
                    </PillToggle>
                  ))}
                </FilterGroup>
                <FilterGroup title={translateLabel("Difficulty")}>
                  {DIFFS.map((d) => (
                    <PillToggle key={d} active={filterControls.difficulty.includes(d)} onClick={() => toggleFilterDraftList("difficulty", d)} testid={`filter-diff-${d}`}>
                      {(DIFF_LABEL_KEYS[d] ? translate(DIFF_LABEL_KEYS[d]) : d)}
                    </PillToggle>
                  ))}
                </FilterGroup>
                <FilterGroup title={translateLabel("Weather")}>
                  <PillToggle
                    active={filterControls.dryWeather}
                    disabled={!searchCenter}
                    onClick={() => updateFilterDraft({ dryWeather: !filterControls.dryWeather })}
                    testid="filter-dry-weather"
                  >
                    {translateLabel("Dry today")}
                  </PillToggle>
                </FilterGroup>
                <p className="-mt-3 mb-5 text-xs leading-5 text-zinc-500">
                  {searchCenter
                    ? "Keeps parks with a dry forecast and low rain risk today."
                    : "Set a location first to use weather filtering."}
                </p>
                <div className="mb-5">
                  <div className="label-tiny mb-2">{translateLabel("Minimum trails: {count}", { count: filterControls.minTrails })}</div>
                  <Slider value={[filterControls.minTrails]} onValueChange={(v) => updateFilterDraft({ minTrails: v[0] })} max={Math.max(12, filterControls.minTrails)} step={1} data-testid="filter-min-trails" />
                </div>
                <div className="mb-5">
                  <div className="label-tiny mb-2">Max driving time: {filterControls.maxDrive ? `${filterControls.maxDrive} min` : "Any"}</div>
                  <Slider value={[filterControls.maxDrive]} onValueChange={(v) => updateFilterDraft({ maxDrive: v[0] })} max={240} step={15} data-testid="filter-max-drive" />
                </div>
                <div className="mb-6">
                  <div className="label-tiny mb-2">{translateLabel("Minimum rating: {value}", { value: filterControls.minRating || translateLabel("Any") })}</div>
                  <Slider value={[filterControls.minRating]} onValueChange={(v) => updateFilterDraft({ minRating: v[0] })} max={5} step={0.5} data-testid="filter-min-rating" />
                </div>
                </div>
                <div className="-mx-6 -mb-6 shrink-0 border-t border-white/10 bg-[#121214]/95 px-6 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-4 backdrop-blur">
                  <button
                    onClick={applyFilterDraft}
                    data-testid="apply-filters-btn"
                    className="mb-2 flex w-full items-center justify-center rounded-full bg-[#CCFF00] py-3 text-sm font-extrabold text-black"
                  >
                    {translateLabel("Apply filters")}
                  </button>
                  <button
                    onClick={clearFilterDraft}
                    data-testid="clear-filters-btn"
                    className="flex w-full items-center justify-center gap-2 rounded-full border border-white/10 py-2.5 text-sm font-bold text-zinc-300 transition-colors hover:bg-white/5"
                  >
                    <XIcon size={16} /> {translateLabel("Clear all filters")}
                  </button>
                </div>
                </SheetContent>
                </Sheet>
                </div>
              </div>
            </div>
          </div>

          <AnimatePresence initial={false}>
            {mobileFiltersOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="shrink-0 overflow-hidden md:hidden"
                data-testid="mobile-inline-filters"
              >
                <div className="mx-3 mb-2 max-h-[52dvh] overflow-y-auto rounded-2xl border border-white/10 bg-[#111113]/95 p-3 shadow-lg backdrop-blur-xl">
                  <div className="mb-3 flex items-center justify-between gap-3 border-b border-white/[0.07] pb-2.5">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-lg font-black uppercase text-white">{translate("discover.filters")}</span>
                      {activeFilterCount > 0 && (
                        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#CCFF00] px-1.5 text-[10px] font-black text-black">
                          {activeFilterCount}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] font-semibold text-zinc-600">{translate("discover.updatesLive")}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-x-3">
                    <FilterGroup title={translateLabel("Availability")} compact>
                      <PillToggle
                        compact
                        active={openNow}
                        onClick={() => updateMobileFilter({ openNow: !openNow })}
                        testid="mobile-filter-open-now"
                      >
                        {translateLabel("Open now")}
                      </PillToggle>
                    </FilterGroup>
                    <FilterGroup title={translateLabel("Weather")} compact>
                      <PillToggle
                        compact
                        active={dryWeather}
                        disabled={!searchCenter}
                        onClick={() => updateMobileFilter({ dryWeather: !dryWeather })}
                        testid="mobile-filter-dry-weather"
                      >
                        {translateLabel("Dry today")}
                      </PillToggle>
                    </FilterGroup>
                  </div>

                  <FilterGroup title={translateLabel("Destination type")} compact>
                    {TYPES.map((t) => (
                      <PillToggle
                        compact
                        key={t.key}
                        active={type === t.key}
                        onClick={() => updateMobileFilter({ type: t.key })}
                        testid={`mobile-filter-type-${t.key}`}
                      >
                        {translate(t.labelKey)}
                      </PillToggle>
                    ))}
                  </FilterGroup>

                  <FilterGroup title={translateLabel("Riding style")} compact>
                    {QUICK_STYLES.map((s) => (
                      <PillToggle
                        compact
                        key={s}
                        active={styles.includes(s)}
                        onClick={() => toggleMobileFilterList("styles", s)}
                        testid={`mobile-filter-style-${s}`}
                      >
                        {STYLE_LABELS[s]}
                      </PillToggle>
                    ))}
                  </FilterGroup>

                  <FilterGroup title={translateLabel("Access")} compact>
                    {Object.keys(ACCESS_LABELS).map((a) => (
                      <PillToggle
                        compact
                        key={a}
                        active={access.includes(a)}
                        onClick={() => toggleMobileFilterList("access", a)}
                        testid={`mobile-filter-access-${a}`}
                      >
                        {ACCESS_LABELS[a]}
                      </PillToggle>
                    ))}
                  </FilterGroup>

                  <FilterGroup title={translateLabel("Difficulty")} compact>
                    {DIFFS.map((d) => (
                      <PillToggle
                        compact
                        key={d}
                        active={difficulty.includes(d)}
                        onClick={() => toggleMobileFilterList("difficulty", d)}
                        testid={`mobile-filter-diff-${d}`}
                      >
                        {(DIFF_LABEL_KEYS[d] ? translate(DIFF_LABEL_KEYS[d]) : d)}
                      </PillToggle>
                    ))}
                  </FilterGroup>

                  <div className="grid grid-cols-2 gap-3 border-t border-white/[0.07] pt-3">
                    <div>
                      <div className="label-tiny mb-2">{translateLabel("Minimum trails: {count}", { count: filterControls.minTrails })}</div>
                      <Slider
                        value={[filterControls.minTrails]}
                        onValueChange={(v) => updateFilterDraft({ minTrails: v[0] })}
                        onValueCommit={(v) => updateMobileFilter({ minTrails: v[0] })}
                        max={Math.max(12, filterControls.minTrails)}
                        step={1}
                        data-testid="mobile-filter-min-trails"
                      />
                    </div>
                    <div>
                      <div className="label-tiny mb-2">
                        {translateLabel("Max drive: {value}", { value: filterControls.maxDrive ? `${filterControls.maxDrive} min` : translateLabel("Any") })}
                      </div>
                      <Slider
                        value={[filterControls.maxDrive]}
                        onValueChange={(v) => updateFilterDraft({ maxDrive: v[0] })}
                        onValueCommit={(v) => updateMobileFilter({ maxDrive: v[0] })}
                        max={240}
                        step={15}
                        data-testid="mobile-filter-max-drive"
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="label-tiny mb-2">{translateLabel("Minimum rating: {value}", { value: filterControls.minRating || translateLabel("Any") })}</div>
                    <Slider
                      value={[filterControls.minRating]}
                      onValueChange={(v) => updateFilterDraft({ minRating: v[0] })}
                      onValueCommit={(v) => updateMobileFilter({ minRating: v[0] })}
                      max={5}
                      step={0.5}
                      data-testid="mobile-filter-min-rating"
                    />
                  </div>

                  {!searchCenter && (
                    <p className="mt-3 text-[11px] leading-4 text-amber-300">
                      {translateLabel("Set a location first to use weather and drive-time filtering.")}
                    </p>
                  )}

                  {activeFilterCount > 0 && (
                    <div className="mt-4 border-t border-white/[0.07] pt-3">
                      <button
                        type="button"
                        onClick={clearMobileFilters}
                        data-testid="mobile-clear-filters-btn"
                        className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-white/10 px-3 py-2.5 text-xs font-bold text-zinc-300 transition-colors hover:bg-white/5"
                      >
                        <XIcon size={14} /> {translateLabel("Clear all filters")}
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {compareSpots.length > 0 && (
            <CompareTray
              spots={compareSpots}
              onRemove={(park) => toggleCompareSpot(park)}
              onClear={() => setCompareSpots([])}
              onCompare={openComparison}
            />
          )}

          {!legacyAiActive && aiDiscovery?.mode === "criteria" && activeCriteria.length > 0 && (
            <div className="mx-3 mb-2 rounded-2xl border border-white/10 bg-[#111113]/92 px-3 py-2.5" data-testid="applied-criteria">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  {aiDiscovery?.mode === "criteria" && (
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#CCFF00]/10 text-[#CCFF00]">
                      <SparkleIcon size={12} weight="fill" />
                    </span>
                  )}
                  <div className="min-w-0">
                    <div className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-zinc-400">
                      {translateLabel(aiDiscovery?.manuallyEdited ? "AI + manual filters" : "AI applied to filters")}
                    </div>
                    {aiDiscovery?.mode === "criteria" && (
                      <p className="mt-0.5 truncate text-[10px] text-zinc-500">
                        {aiDiscovery.manuallyEdited
                          ? translateLabel("Started from AI criteria; you changed the filters manually.")
                          : aiDiscovery.explanation}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={openCriteriaEditor}
                  className="shrink-0 text-[10px] font-bold text-[#CCFF00] transition-colors hover:text-white"
                  data-testid="edit-applied-criteria"
                >
                  {translateLabel("Edit filters")}
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {activeCriteria.map((criterion) => (
                  <span
                    key={criterion.id}
                    data-testid={`criterion-${criterion.id}`}
                    className="inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-black/25 px-2.5 py-1 text-[10px] font-bold text-zinc-300"
                  >
                    {translateLabel(criterion.label)}
                    {criterion.removable !== false && (
                      <button
                        type="button"
                        onClick={() => removeActiveCriterion(criterion)}
                        aria-label={`Remove ${translateLabel(criterion.label)}`}
                        className="-mr-1 flex h-4 w-4 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-white/10 hover:text-white"
                      >
                        <XIcon size={9} weight="bold" />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {legacyAiActive && (
            <div className="mx-3 mb-2 rounded-2xl border border-[#CCFF00]/20 bg-[#CCFF00]/[0.055] px-3 py-2.5">
              <div className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#CCFF00]/10 text-[#CCFF00]">
                  <SparkleIcon size={14} weight="fill" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#CCFF00]">
                      {translateLabel(aiDiscovery.matchKind === "UNVERIFIED" ? "Advanced AI · unverified candidates" : "Advanced AI results")}
                    </span>
                    <button
                      type="button"
                      onClick={clearAiResults}
                      className="shrink-0 text-[10px] font-bold text-zinc-500 transition-colors hover:text-white"
                    >
                      {translateLabel("Clear advanced")}
                    </button>
                  </div>
                  <p className="mt-1 text-[10px] leading-4 text-zinc-500">
                    {translateLabel("Legacy result · advanced criteria are not encoded in the shared filter URL.")}
                  </p>
                  {aiDiscovery.unverifiedReasons?.length > 0 && (
                    <div className="mt-1 text-[11px] leading-4 text-amber-200">
                      {aiDiscovery.unverifiedReasons.map((reason) => <p key={reason}>{reason}</p>)}
                    </div>
                  )}
                  {aiDiscovery.evaluationComplete === false && (
                    <p className="mt-1 text-[11px] leading-4 text-zinc-400">
                      {translateLabel("Candidate evaluation was bounded; results are not an exhaustive statement about every destination.")}
                    </p>
                  )}
                  {(aiDiscovery.origins?.length > 1 || aiDiscovery.intent?.max_drive_minutes) && (
                    <p className="mt-1 text-[11px] leading-4 text-zinc-400">
                      {aiDiscovery.origins?.length > 1
                        ? `From ${aiDiscovery.origins.map((item) => item.label).join(" + ")}`
                        : null}
                      {aiDiscovery.origins?.length > 1 && aiDiscovery.intent?.max_drive_minutes ? " · " : ""}
                      {aiDiscovery.intent?.max_drive_minutes ? translateLabel("Max drive: {value}", { value: `${aiDiscovery.intent.max_drive_minutes} min` }) : ""}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Results list */}
          <div
            ref={listRef}
            className={`flex-1 space-y-3 overflow-y-auto px-3 ${
              compareSpots.length > 0
                ? "pb-[calc(10rem+env(safe-area-inset-bottom))] md:pb-28"
                : "pb-[calc(6rem+env(safe-area-inset-bottom))]"
            } ${
              desktopResultsView === "list" ? "md:space-y-2" : "md:px-4"
            }`}
            data-testid="results-list"
            data-desktop-view={desktopResultsView}
          >
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Skeleton
                  key={i}
                  className={`h-56 w-full rounded-2xl bg-white/5 sm:h-64 ${desktopResultsView === "list" ? "md:h-32" : ""}`}
                />
              ))
            ) : parks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <MountainsIcon size={40} className="mb-3 text-zinc-600" />
                <p className="font-display text-xl font-bold text-white">{translate("discover.noRides")}</p>
                <p className="mt-1 text-sm text-zinc-400">{translate("discover.widenRadius")}</p>
              </div>
            ) : (
              parks.map((p, i) => (
                <ParkCard
                  key={p.id}
                  park={p}
                  index={i}
                  active={selectedId === p.id}
                  onHover={setHoveredId}
                  onSelect={handleListSelect}
                  desktopCompact={desktopResultsView === "list"}
                  compareSelected={compareSpots.some((candidate) => candidate.id === p.id)}
                  onToggleCompare={toggleCompareSpot}
                  discoveryCriteria={resultEvidenceCriteria}
                  discoveryPreferences={resultEvidencePreferences}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CompareTray({ spots, onRemove, onClear, onCompare }) {
  return (
    <div
      className="pointer-events-none absolute inset-x-2 bottom-2 z-40 flex justify-center md:inset-x-3 md:bottom-3"
      data-testid="compare-tray"
    >
      <div className="pointer-events-auto w-full rounded-2xl border border-white/10 bg-[#0b0b0d]/95 p-2.5 shadow-[0_18px_60px_rgba(0,0,0,0.42)] backdrop-blur-xl md:flex md:items-center md:gap-2 md:px-3 md:py-2.5">
        <div className="flex items-center justify-between gap-3 md:shrink-0">
          <div className="flex items-baseline gap-2">
            <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#CCFF00]">
              {translateLabel("Compare")}
            </span>
            <span className="text-[11px] font-semibold text-zinc-500">{spots.length}/{MAX_COMPARE_SPOTS}</span>
          </div>
          <button
            type="button"
            onClick={onClear}
            className="text-[11px] font-bold text-zinc-500 transition-colors hover:text-white md:hidden"
          >
            {translateLabel("Clear")}
          </button>
        </div>

        <div className="mt-2 flex min-w-0 gap-1.5 overflow-x-auto bpf-scrollbar-hidden md:mt-0 md:flex-1">
          {spots.map((park) => (
            <button
              key={park.id}
              type="button"
              onClick={() => onRemove(park)}
              aria-label={translateLabel("Remove {name} from comparison", { name: park.name })}
              className="inline-flex max-w-[10rem] shrink-0 items-center gap-1.5 rounded-lg border border-white/[0.07] bg-white/[0.045] px-2.5 py-1.5 text-[11px] font-semibold text-zinc-300 transition-colors hover:border-white/15 hover:bg-white/[0.08]"
            >
              <span className="truncate">{park.name}</span>
              <XIcon size={11} className="shrink-0 text-zinc-500" />
            </button>
          ))}
        </div>

        <div className="mt-2 flex items-center gap-2 md:mt-0 md:shrink-0">
          <button
            type="button"
            onClick={onClear}
            className="hidden shrink-0 px-1.5 py-1 text-[11px] font-bold text-zinc-500 transition-colors hover:text-white md:block"
          >
            {translateLabel("Clear")}
          </button>
          <button
            type="button"
            onClick={onCompare}
            disabled={spots.length < 2}
            data-testid="compare-spots-btn"
            className="flex-1 rounded-xl bg-[#CCFF00] px-3.5 py-2 text-xs font-extrabold text-black transition-all hover:bg-[#B8E600] disabled:cursor-not-allowed disabled:opacity-35 md:flex-none"
          >
            {translateLabel("Compare")}
          </button>
        </div>
      </div>
    </div>
  );
}

function MapSelectionPreview({
  park,
  unitSystem,
  discoveryCriteria,
  discoveryPreferences,
  onClose,
}) {
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const trailCount = park.trail_count ?? 0;
  const locationLabel = park.distance_km != null
    ? formatDistanceKm(park.distance_km, unitSystem)
    : [park.city, translateLabel(park.country)].filter(Boolean).join(", ") || translateLabel("Location unavailable");
  const evidence = buildDiscoveryResultEvidence(park, discoveryCriteria, discoveryPreferences);
  const evidenceCount = evidence.matches.length + evidence.preferenceMatches.length;
  const hasEvidence = evidenceCount > 0 || evidence.warnings.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 18 }}
      transition={{ duration: 0.2 }}
      className="mb-2 w-[min(20rem,calc(100vw-2rem))]"
      data-testid="mobile-map-selection-preview"
    >
      <div className="relative overflow-hidden rounded-2xl border border-white/15 bg-black/90 p-2 shadow-2xl backdrop-blur-2xl">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
          aria-label={translateLabel("Close {name} preview", { name: park.name })}
          className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-zinc-300 transition-colors hover:bg-white/20 hover:text-white"
          data-testid="close-map-selection-preview"
        >
          <XIcon size={16} weight="bold" />
        </button>

        <div className="flex items-stretch gap-3 pr-8 text-left">
          <img
            src={destinationCoverImage(park)}
            alt=""
            className="h-20 w-20 shrink-0 rounded-xl object-cover"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = destinationFallbackImage(park);
            }}
          />
          <div className="min-w-0 py-1">
            <div className="text-xs font-bold uppercase tracking-wide text-[#CCFF00]">
              {translateLabel(destinationTypeLabel(park.type))}
            </div>
            <div className="mt-0.5 truncate font-display text-lg font-bold leading-tight text-white">
              {park.name}
            </div>
            <div className="mt-1.5 flex min-w-0 items-center gap-3 text-xs font-semibold text-zinc-400">
              <span className="flex min-w-0 items-center gap-1">
                <MapPinIcon size={13} className="shrink-0" />
                <span className="truncate">{locationLabel}</span>
              </span>
              <span className="flex shrink-0 items-center gap-1">
                <PathIcon size={13} /> {trailCount}
              </span>
            </div>

            {hasEvidence && (
              <div className="mt-2 flex items-center gap-2 text-[11px] leading-4">
                {evidenceCount > 0 && (
                  <span className="font-semibold text-zinc-400">
                    {translateLabel(evidenceCount === 1 ? "Matches {count} criterion" : "Matches {count} criteria", { count: evidenceCount })}
                  </span>
                )}
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setEvidenceOpen((value) => !value);
                  }}
                  className="font-extrabold text-[#CCFF00] transition-colors hover:text-white"
                  aria-expanded={evidenceOpen}
                  data-testid="map-why-result-toggle"
                >
                  {translateLabel("Why?")}
                </button>
                {evidence.warnings.length > 0 && (
                  <span className="text-amber-300" title={evidence.warnings.join(" · ")}>⚠</span>
                )}
              </div>
            )}

            <Link
              to={parkPath(park)}
              onClick={(event) => event.stopPropagation()}
              className="mt-2 flex items-center gap-1 text-xs font-bold text-white"
              data-testid="open-selected-destination"
            >
              {translateLabel("View destination")} <ArrowRightIcon size={13} weight="bold" />
            </Link>
          </div>
        </div>

        {evidenceOpen && (
          <div
            className="mt-2 max-h-44 space-y-2 overflow-y-auto rounded-xl border border-white/10 bg-white/[0.035] p-2.5 text-[11px] leading-4"
            data-testid="map-why-result"
          >
            {evidence.matches.length > 0 && (
              <div>
                <div className="font-extrabold uppercase tracking-[0.12em] text-[#CCFF00]">{translate("discover.matches")}</div>
                <ul className="mt-1 space-y-1 text-zinc-300">
                  {evidence.matches.map((reason) => <li key={reason}>• {reason}</li>)}
                </ul>
              </div>
            )}
            {evidence.preferenceMatches.length > 0 && (
              <div>
                <div className="font-extrabold uppercase tracking-[0.12em] text-zinc-500">{translate("discover.preferenceFit")}</div>
                <ul className="mt-1 space-y-1 text-zinc-400">
                  {evidence.preferenceMatches.map((reason) => <li key={reason}>• {reason}</li>)}
                </ul>
              </div>
            )}
            {evidence.warnings.length > 0 && (
              <div className="space-y-1 text-amber-200">
                {evidence.warnings.map((warning) => <p key={warning}>⚠ {warning}</p>)}
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function SearchGroup({ title, visible, children }) {
  if (!visible) return null;
  return (
    <div className="py-1" data-testid={`search-group-${title.toLowerCase()}`}>
      <div className="px-4 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{title}</div>
      {children}
    </div>
  );
}

function SearchResultButton({ title, subtitle, icon, onClick, testid, to }) {
  const content = (
    <>
      <span className="mt-0.5 shrink-0 text-[#CCFF00]">{icon}</span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold text-zinc-100">{title}</span>
        {subtitle && <span className="block truncate text-xs text-zinc-500">{subtitle}</span>}
      </span>
    </>
  );

  const className = "flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors hover:bg-white/5";

  if (to) {
    return (
      <Link
        to={to}
        onClick={onClick}
        data-testid={testid}
        className={className}
      >
        {content}
      </Link>
    );
  }

  return (
    <button onClick={onClick} data-testid={testid} className={className}>
      {content}
    </button>
  );
}

function FilterGroup({ title, children, compact = false }) {
  return (
    <div className={compact ? "mb-3" : "mb-5"}>
      <div className={`label-tiny ${compact ? "mb-1.5 text-[9px]" : "mb-2"}`}>{title}</div>
      <div className={`flex flex-wrap ${compact ? "gap-1.5" : "gap-2"}`}>{children}</div>
    </div>
  );
}

function PillToggle({ active, onClick, children, testid, disabled = false, compact = false }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`rounded-full font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${
        compact ? "px-2.5 py-1 text-[11px]" : "px-3.5 py-1.5 text-sm"
      } ${
        active ? "bg-[#CCFF00] text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10"
      }`}
    >
      {children}
    </button>
  );
}
